/**
 * DecisionFlow — PlanAssignmentManager
 * data-fit LLC · Confidential
 * Stage 3: DATA SETUP — Screen SETUP-06
 * Spec authority: ComponentSpec_DATASETUP_v1_corrected.md (Session 90)
 * MasterContext authority: MasterContext_Session90_Close.md
 * Generated: Session 90 — ChatGPT Phase 3
 * Patched: S205 — CSS fix: df-* classes replaced with Tailwind utilities (no new CSS files)
 * Path: ui/src/components/tenant-admin/PlanAssignmentManager.jsx
 * M040 C2: hardcoded colors → tokens, text-* → text-tok-*, all <td> get text-[rgb(var(--color-text))]
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { API_BASE } from './DataSetupFlowShell';
import { Lock } from 'lucide-react';

const EMPTY_FORM = {
  position_id: '', fiscal_period_id: '', plan_code: '',
  plan_component_id: '', business_unit_id: '', effective_date: '', end_date: ''
};

function toIntegerOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getErrorMessage(error, fallback) {
  return error instanceof Error && error.message ? error.message : fallback;
}

function PlanAssignmentManager({ tier = 'essentials', onBack, onNext, isLocked }) {
  const { user } = useAuth();

  const [assignments, setAssignments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [planComponents, setPlanComponents] = useState([]);
  const [fiscalPeriods, setFiscalPeriods] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
  const [supersedeEndDate, setSupersedeEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [referenceLoading, setReferenceLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState('');
  const [blockingError, setBlockingError] = useState('');
  const [toast, setToast] = useState(null);

  // ── CSV bulk-import state (NN-VALIDATE: dry-run → confirm → commit) ──────
  const [csvFile, setCsvFile]             = useState(null);
  const [csvDryResult, setCsvDryResult]   = useState(null);
  const [csvCommitted, setCsvCommitted]   = useState(false);
  const [csvImporting, setCsvImporting]   = useState(false);
  const [csvError, setCsvError]           = useState('');

  const hasBusinessUnitScope = tier === 'standard' || tier === 'enterprise';

  const fetchJson = useCallback(async (url, options = {}) => {
    const response = await fetch(url, { ...options, credentials: 'include', headers: { ...(options.headers || {}) } });
    if (!response.ok) { const message = await response.text(); throw new Error(message || `Request failed with status ${response.status}`); }
    if (response.status === 204) return null;
    return response.json();
  }, []);

  const showToast = useCallback((message) => { setToast(message); window.setTimeout(() => setToast(null), 3000); }, []);

  const loadAssignments = useCallback(async () => {
    if (!user) return; setLoading(true); setInlineError('');
    try { const data = await fetchJson(`${API_BASE}/plan-assignments`); setAssignments(Array.isArray(data) ? data : data?.plan_assignments || data?.data || []); }
    catch (error) { setInlineError(getErrorMessage(error, 'Unable to fetch plan assignments.')); }
    finally { setLoading(false); }
  }, [fetchJson, user]);

  const loadPlanComponents = useCallback(async () => {
    if (!user) return;
    try { const data = await fetchJson(`${API_BASE}/plan-components`); setPlanComponents(Array.isArray(data) ? data : data?.plan_components || data?.data || []); }
    catch (error) { setInlineError(getErrorMessage(error, 'Plan component endpoint unavailable or unconfirmed.')); }
  }, [fetchJson, user]);

  const loadReferenceData = useCallback(async () => {
    if (!user) return; setReferenceLoading(true); setBlockingError('');
    try {
      const [positionsData, plansData, fiscalPeriodsData, businessUnitsData] = await Promise.all([
        fetchJson(`${API_BASE}/positions`), fetchJson(`${API_BASE}/plans`),
        fetchJson(`${API_BASE}/fiscal-periods`),
        hasBusinessUnitScope ? fetchJson(`${API_BASE}/business-units`) : Promise.resolve([])
      ]);
      setPositions(Array.isArray(positionsData) ? positionsData : positionsData?.positions || positionsData?.data || []);
      setPlans(Array.isArray(plansData) ? plansData : plansData?.plans || plansData?.data || []);
      setFiscalPeriods(Array.isArray(fiscalPeriodsData) ? fiscalPeriodsData : fiscalPeriodsData?.fiscal_periods || fiscalPeriodsData?.data || []);
      setBusinessUnits(Array.isArray(businessUnitsData) ? businessUnitsData : businessUnitsData?.business_units || businessUnitsData?.data || []);
    } catch (error) { setBlockingError(getErrorMessage(error, 'Unable to fetch required assignment reference data.')); }
    finally { setReferenceLoading(false); }
  }, [fetchJson, hasBusinessUnitScope, user]);

  useEffect(() => { loadReferenceData(); }, [loadReferenceData]);
  useEffect(() => { loadPlanComponents(); }, [loadPlanComponents]);
  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  const updateForm = useCallback((field, value) => { setForm((previous) => ({ ...previous, [field]: value })); }, []);
  const resetForm  = useCallback(() => { setForm(EMPTY_FORM); setInlineError(''); }, []);

  const validateCreateForm = useCallback(() => {
    if (!form.position_id) return 'Position is required.';
    if (!form.fiscal_period_id) return 'Fiscal period is required.';
    if (!form.plan_code.trim()) return 'Plan code is required.';
    if (!form.plan_component_id) return 'Plan component is required.';
    if (!form.effective_date) return 'Effective date is required.';
    return '';
  }, [form]);

  const buildCreatePayload = useCallback(() => ({
    position_id: form.position_id || null,
    fiscal_period_id: toIntegerOrNull(form.fiscal_period_id),
    plan_code: form.plan_code.trim(),
    plan_component_id: toIntegerOrNull(form.plan_component_id),
    business_unit_id: hasBusinessUnitScope ? toIntegerOrNull(form.business_unit_id) : null,
    effective_date: form.effective_date,
    end_date: form.end_date || null
  }), [form, hasBusinessUnitScope]);

  // ── CSV bulk-import handlers ────────────────────────────────────────────

  const runCsvDryRun = useCallback(async (file) => {
    if (!file) return;
    setCsvImporting(true);
    setCsvError('');
    setCsvDryResult(null);
    setCsvCommitted(false);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await fetch(
        `${API_BASE}/plan-assignments/bulk-import?dry_run=true`,
        { method: 'POST', credentials: 'include', body: form }
      );
      const data = await res.json();
      if (!res.ok) {
        const msg = data?.detail?.error?.message || data?.detail || `Error ${res.status}`;
        setCsvError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        return;
      }
      setCsvDryResult(data);
    } catch (err) {
      setCsvError(err.message || 'Dry-run request failed.');
    } finally {
      setCsvImporting(false);
    }
  }, []);

  const runCsvCommit = useCallback(async (file) => {
    if (!file) return;
    setCsvImporting(true);
    setCsvError('');
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await fetch(
        `${API_BASE}/plan-assignments/bulk-import?dry_run=false`,
        { method: 'POST', credentials: 'include', body: form }
      );
      const data = await res.json();
      if (!res.ok) {
        const msg = data?.detail?.error?.message || data?.detail || `Error ${res.status}`;
        setCsvError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        return;
      }
      setCsvCommitted(true);
      setCsvDryResult(null);
      setCsvFile(null);
      showToast(`${data.assignments_created ?? 0} plan assignment(s) loaded.`);
      await loadAssignments();
    } catch (err) {
      setCsvError(err.message || 'Commit request failed.');
    } finally {
      setCsvImporting(false);
    }
  }, [loadAssignments, showToast]);

  const createAssignment = useCallback(async (event) => {
    event.preventDefault();
    const validationMessage = validateCreateForm();
    if (validationMessage) { setInlineError(validationMessage); return; }
    setSaving(true); setInlineError('');
    try {
      const created = await fetchJson(`${API_BASE}/plan-assignments`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(buildCreatePayload()) });
      if (created) { setAssignments((previous) => [created, ...previous]); } else { await loadAssignments(); }
      resetForm(); showToast('Plan assignment created.');
    } catch (error) { setInlineError(getErrorMessage(error, 'Unable to create assignment. Confirm the plan_code resolves server-side.')); }
    finally { setSaving(false); }
  }, [buildCreatePayload, fetchJson, loadAssignments, resetForm, showToast, validateCreateForm]);

  const supersedeAssignment = useCallback(async (assignmentId) => {
    if (!supersedeEndDate) { setInlineError('End date is required to supersede an assignment.'); return; }
    setSaving(true); setInlineError('');
    try {
      const payload = { status: 'superseded', end_date: supersedeEndDate };
      const updated = await fetchJson(`${API_BASE}/plan-assignments/${assignmentId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      setAssignments((previous) => previous.map((assignment) => assignment.plan_assignment_id === assignmentId ? { ...assignment, ...(updated || payload) } : assignment));
      setSelectedAssignmentId(null); setSupersedeEndDate('');
      showToast('Assignment superseded. Create a new assignment for the new plan.');
    } catch (error) { setInlineError(getErrorMessage(error, 'Unable to supersede assignment.')); }
    finally { setSaving(false); }
  }, [fetchJson, showToast, supersedeEndDate]);

  const approvedPlans = useMemo(() => plans.filter((plan) => plan.plan_status === 'approved' || plan.approved_by), [plans]);
  const approvedPlanComponents = useMemo(() => planComponents.filter((component) => component.approved_by), [planComponents]);

  const positionNameById = useMemo(() => positions.reduce((acc, p) => { acc[String(p.position_id)] = p.full_name || p.person_uuid || `Position ${p.position_id}`; return acc; }, {}), [positions]);
  const fiscalPeriodNameById = useMemo(() => fiscalPeriods.reduce((acc, p) => { acc[String(p.fiscal_period_id)] = p.period_label || `Period ${p.fiscal_period_id}`; return acc; }, {}), [fiscalPeriods]);
  const businessUnitNameById = useMemo(() => businessUnits.reduce((acc, bu) => { acc[String(bu.business_unit_id)] = bu.business_unit_name || bu.name; return acc; }, {}), [businessUnits]);
  const planComponentLabelById = useMemo(() => planComponents.reduce((acc, c) => { acc[String(c.plan_component_id)] = c.component_name || c.name || `Component ${c.plan_component_id}`; return acc; }, {}), [planComponents]);

  // Shared class strings — M040 tokens
  const thClass    = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
  const tdClass    = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
  const btnPrimary    = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
  const btnSecondary  = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
  const inputClass    = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none';
  const labelClass    = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

  if (!user) {
    return (
      <section className="flex flex-col gap-6">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Plan Assignment Manager</h2>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">Authentication is required to manage plan assignments.</div>
      </section>
    );
  }

  if (blockingError) {
    return (
      <section className="flex flex-col gap-6">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Plan Assignment Manager</h2>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{blockingError}</div>
        <button type="button" className={btnSecondary} onClick={loadReferenceData}>Retry</button>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-text-muted))] mb-1">SETUP-06</p>
          <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Plan Assignment Manager</h2>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">Assign approved plans and components to positions. Mid-year changes supersede the old assignment and create a new one.</p>
        </div>
      </header>

      {toast ? <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">{toast}</div> : null}
      {inlineError ? <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{inlineError}</div> : null}

      {approvedPlans.length === 0 || approvedPlanComponents.length === 0 ? (
        <div role="status" className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">No approved plans/components are available. Assignment creation may be blocked by server-side validation.</div>
      ) : null}

      {/* ── CSV Bulk Import (NN-SCALE: primary ingestion path) ──────────────── */}
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
        <div>
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">CSV Bulk Import</h3>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Upload <strong>PlanAssignments.csv</strong> to assign all positions in one pass.
            Validates before committing — review results before confirming.
          </p>
          <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Required columns: <code>employee_id, plan_code, fiscal_period_code, business_unit_code, effective_date</code>
          </p>
        </div>

        {csvError ? (
          <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
            {csvError}
          </div>
        ) : null}

        {/* S603 OI-LOCKED-PERIOD-WRITE-SUPPRESSION — matches CalculationPanel pattern exactly */}
        {isLocked ? (
          <div className="flex items-center gap-2 rounded-xl border border-amber-300/30 bg-amber-400/8 px-3 py-2.5">
            <Lock className="h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-tok-sm text-amber-600">
              Period is locked — assignment imports are not available. Switch to an open period to import assignments.
            </p>
          </div>
        ) : (
        <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="file"
              accept=".csv"
              className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setCsvFile(f);
                setCsvDryResult(null);
                setCsvCommitted(false);
                setCsvError('');
              }}
            />
            <button
              type="button"
              className={btnSecondary}
              disabled={!csvFile || csvImporting}
              onClick={() => runCsvDryRun(csvFile)}
            >
              {csvImporting && !csvDryResult ? 'Validating…' : 'Validate'}
            </button>
            {csvCommitted ? (
              <span className="text-tok-sm text-[rgb(var(--color-success))]">✓ Loaded</span>
            ) : null}
          </div>

          {csvDryResult ? (
            <div className="flex flex-col gap-2">
              <div className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                Rows parsed: <strong>{csvDryResult.validated}</strong> ·
                Assignments to create: <strong>{csvDryResult.assignments_would_create}</strong>
              </div>

              {csvDryResult.errors?.length > 0 ? (
                <div>
                  <p className="mb-1 text-tok-xs font-semibold text-[rgb(var(--color-danger))]">
                    {csvDryResult.errors.length} validation error(s) — fix CSV and re-upload:
                  </p>
                  <div className="overflow-x-auto rounded-lg border border-[rgb(var(--color-danger))]/30">
                    <table className="min-w-full text-tok-xs">
                      <thead>
                        <tr className="bg-[rgb(var(--color-danger))]/5">
                          <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Row</th>
                          <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Field</th>
                          <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvDryResult.errors.map((err, idx) => (
                          <tr key={idx} className="border-t border-[rgb(var(--color-border))]">
                            <td className="px-3 py-1.5 text-[rgb(var(--color-text))]">{err.row ?? '—'}</td>
                            <td className="px-3 py-1.5 font-mono text-[rgb(var(--color-text))]">{err.field}</td>
                            <td className="px-3 py-1.5 text-[rgb(var(--color-danger))]">{err.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-tok-sm text-[rgb(var(--color-success))]">
                    ✓ Validation passed — {csvDryResult.assignments_would_create} assignment(s) ready to load.
                  </span>
                  <button
                    type="button"
                    className={btnPrimary}
                    disabled={csvImporting}
                    onClick={() => runCsvCommit(csvFile)}
                  >
                    {csvImporting ? 'Loading…' : 'Confirm and load'}
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>
        )}
      </div>

      {/* ── Single-row form (exception path) ─────────────────────────────────── */}
      {/* S603 OI-LOCKED-PERIOD-WRITE-SUPPRESSION */}
      {isLocked ? (
        <div className="flex items-center gap-2 rounded-xl border border-amber-300/30 bg-amber-400/8 px-3 py-2.5">
          <Lock className="h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-tok-sm text-amber-600">
            Period is locked — assignment creation is not available. Switch to an open period to create assignments.
          </p>
        </div>
      ) : (
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
        <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Add single assignment <span className="text-tok-sm font-normal text-[rgb(var(--color-text-muted))]">(exception path — use CSV import above for bulk loads)</span></h3>
        <form onSubmit={createAssignment}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className={labelClass}>Position
              <select className={inputClass} value={form.position_id} onChange={(e) => updateForm('position_id', e.target.value)} disabled={referenceLoading}>
                <option value="">Select position</option>
                {positions.map((p) => <option key={p.position_id} value={p.position_id}>{p.full_name || p.person_uuid || `Position ${p.position_id}`}</option>)}
              </select>
            </label>
            <label className={labelClass}>Fiscal period
              <select className={inputClass} value={form.fiscal_period_id} onChange={(e) => updateForm('fiscal_period_id', e.target.value)} disabled={referenceLoading}>
                <option value="">Select fiscal period</option>
                {fiscalPeriods.map((p) => <option key={p.fiscal_period_id} value={p.fiscal_period_id}>{p.period_label || `Period ${p.fiscal_period_id}`}{p.is_active ? ' · active' : ''}</option>)}
              </select>
            </label>
            <label className={labelClass}>Plan code
              <input type="text" className={inputClass} value={form.plan_code} onChange={(e) => updateForm('plan_code', e.target.value)} list="approved-plan-codes" />
              <datalist id="approved-plan-codes">{approvedPlans.map((plan) => <option key={plan.plan_id} value={plan.plan_code}>{plan.plan_name}</option>)}</datalist>
            </label>
            <label className={labelClass}>Plan component
              <select className={inputClass} value={form.plan_component_id} onChange={(e) => updateForm('plan_component_id', e.target.value)}>
                <option value="">Select approved component</option>
                {approvedPlanComponents.map((c) => <option key={c.plan_component_id} value={c.plan_component_id}>{c.component_name || c.name || `Component ${c.plan_component_id}`}</option>)}
              </select>
            </label>
            {hasBusinessUnitScope ? (
              <label className={labelClass}>Business unit
                <select className={inputClass} value={form.business_unit_id} onChange={(e) => updateForm('business_unit_id', e.target.value)}>
                  <option value="">No BU / global</option>
                  {businessUnits.map((bu) => <option key={bu.business_unit_id} value={bu.business_unit_id}>{bu.business_unit_name || bu.name}</option>)}
                </select>
              </label>
            ) : null}
            <label className={labelClass}>effective_date<input type="date" className={inputClass} value={form.effective_date} onChange={(e) => updateForm('effective_date', e.target.value)} /></label>
            <label className={labelClass}>end_date<input type="date" className={inputClass} value={form.end_date} onChange={(e) => updateForm('end_date', e.target.value)} /></label>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <button type="submit" className={btnPrimary} disabled={saving}>{saving ? 'Saving…' : 'Create assignment'}</button>
            <button type="button" className={btnSecondary} onClick={resetForm} disabled={saving}>Clear</button>
          </div>
        </form>
      </div>
      )}

      {/* Assignments table */}
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Current assignments</h3>
          {loading ? <span className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</span> : null}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className={btnSecondary} onClick={loadAssignments} disabled={loading}>Refresh assignments</button>
        </div>
        {assignments.length === 0 && !loading ? (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No plan assignments have been created.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-tok-sm border-collapse">
              <thead>
                <tr>
                  <th className={thClass}>Position</th>
                  <th className={thClass}>Fiscal period</th>
                  <th className={thClass}>Plan code</th>
                  <th className={thClass}>Component</th>
                  {hasBusinessUnitScope ? <th className={thClass}>Business unit</th> : null}
                  <th className={thClass}>effective_date</th>
                  <th className={thClass}>end_date</th>
                  <th className={thClass}>Status</th>
                  <th className={thClass} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {assignments.map((assignment) => (
                  <tr key={assignment.plan_assignment_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                    <td className={tdClass}>{positionNameById[String(assignment.position_id)] || assignment.position_id}</td>
                    <td className={tdClass}>{fiscalPeriodNameById[String(assignment.fiscal_period_id)] || assignment.fiscal_period_id}</td>
                    <td className={tdClass}>{assignment.plan_code || assignment.plan_id}</td>
                    <td className={tdClass}>{planComponentLabelById[String(assignment.plan_component_id)] || assignment.plan_component_id}</td>
                    {hasBusinessUnitScope ? <td className={tdClass}>{businessUnitNameById[String(assignment.business_unit_id)] || assignment.business_unit_id || 'Global'}</td> : null}
                    <td className={tdClass}>{assignment.effective_date || '—'}</td>
                    <td className={tdClass}>{assignment.end_date || '—'}</td>
                    <td className={tdClass}>{assignment.status || 'active'}</td>
                    <td className={tdClass}>
                      {selectedAssignmentId === assignment.plan_assignment_id ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <input type="date" className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-1.5 text-tok-sm focus:outline-none" value={supersedeEndDate} onChange={(e) => setSupersedeEndDate(e.target.value)} aria-label="Supersede end date" />
                          <button type="button" className={btnPrimary} onClick={() => supersedeAssignment(assignment.plan_assignment_id)} disabled={saving}>Confirm supersede</button>
                          <button type="button" className={btnSecondary} onClick={() => { setSelectedAssignmentId(null); setSupersedeEndDate(''); }}>Cancel</button>
                        </div>
                      ) : (
                        <button type="button" className={btnSecondary} onClick={() => setSelectedAssignmentId(assignment.plan_assignment_id)} disabled={assignment.status === 'superseded'}>Supersede</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <footer className="flex items-center gap-3 pt-4 border-t border-[rgb(var(--color-border))]">
        <button type="button" className={btnSecondary} onClick={onBack}>Back</button>
        <button type="button" className={btnPrimary} onClick={onNext}>Continue to readiness</button>
      </footer>
    </section>
  );
}

export default PlanAssignmentManager;
